import { axiosInstanceTasks } from "../axios";
import {
  ClassName,
  RequestResponse,
  TaskCreate,
  TaskCreations,
  TaskExecute
} from "../types";

export const createTask = async (
  payload: Omit<TaskCreate, "nextRunTimee" | "nextRunDate">
): Promise<RequestResponse> => {
  try {
    await axiosInstanceTasks.post("/tasks", payload);
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to create task: ", err);
    return {
      error: true,
      message: err.response?.data?.error_description?.includes(
        "Access is denied"
      )
        ? "Access denied: You do not have permission to perform this activity."
        : err.response?.data?.error_description ||
          err.response?.data?.message ||
          err.message,
      data: err.response?.data.data || null
    };
  }
};

export const excuteTasks = async (
  payload: TaskExecute
): Promise<RequestResponse> => {
  try {
    await axiosInstanceTasks.post("/tasks/exec", payload);
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to execute task: ", err);
    return {
      error: true,
      message: err.response?.data?.error_description?.includes(
        "Access is denied"
      )
        ? "Access denied: You do not have permission to perform this activity."
        : err.response?.data?.error_description ||
          err.response?.data?.message ||
          err.message,
      data: err.response?.data.data || null
    };
  }
};

export const getTasks = async (
  pageNumber: number,
  pageSize: number,
  state: string | undefined,
  taskName: string | undefined,
  className: string | undefined
): Promise<RequestResponse<{ records: TaskCreations[]; total: number }>> => {
  try {
    const params = {
      page: pageNumber,
      size: pageSize,
      state,
      taskName,
      className
    };
    const response = await axiosInstanceTasks.get("/tasks-paginated", {
      params
    });

    return {
      error: false,
      data: {
        records: response.data.data.records,
        total: response.data.data.total
      }
    };
  } catch (err) {
    console.error("Unable to fetch task: ", err);
    return {
      error: true,
      message: err.response?.data?.error_description?.includes(
        "Access is denied"
      )
        ? "Access denied: You do not have permission to perform this activity."
        : err.response?.data?.error_description ||
          err.response?.data?.message ||
          err.message,
      data: null
    };
  }
};

export const updateTask = async (
  id: number,
  payload: TaskCreate
): Promise<RequestResponse> => {
  try {
    await axiosInstanceTasks.patch(`/tasks/${id}`, payload);
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to update task: ", err);
    return {
      error: true,
      message: err.response?.data?.error_description?.includes(
        "Access is denied"
      )
        ? "Access denied: You do not have permission to perform this activity."
        : err.response?.data?.error_description ||
          err.response?.data?.message ||
          err.message,
      data: err.response?.data.data || null
    };
  }
};

export const deleteTask = async (
  taskName: string
): Promise<RequestResponse> => {
  try {
    await axiosInstanceTasks.delete(`/tasks/name/${taskName}`);
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to delete task: ", err);
    return {
      error: true,
      message: err.response?.data?.error_description?.includes(
        "Access is denied"
      )
        ? "Access denied: You do not have permission to perform this activity."
        : err.response?.data?.error_description ||
          err.response?.data?.message ||
          err.message ||
          err,
      data: err.response?.data.data || null
    };
  }
};

export const fetchClassNames = async (): Promise<
  RequestResponse<ClassName[]>
> => {
  try {
    const response = await axiosInstanceTasks.get("/package-class");
    const classNamesData = response.data.data.map((item: any) => ({
      className: item.className,
      fullClass: item.fullClass
    }));
    return {
      error: false,
      data: classNamesData
    };
  } catch (err) {
    console.error("Unable to fetch class names: ", err);
    return {
      error: true,
      message: err.response?.data?.error_description?.includes(
        "Access is denied"
      )
        ? "Access denied: You do not have permission to perform this activity."
        : err.response?.data?.error_description ||
          err.response?.data?.message ||
          err.message,
      data: []
    };
  }
};

export const fetchClassNamesForFileUpload = async (): Promise<
  RequestResponse<ClassName[]>
> => {
  try {
    const response = await axiosInstanceTasks.get("/upload/modules");
    const classNamesData = response.data.data.map((item: any) => ({
      className: item.className,
      fullClass: item.fullClass
    }));
    return {
      error: false,
      data: classNamesData
    };
  } catch (err) {
    console.error("Unable to fetch class names: ", err);
    return {
      error: true,
      message: err.response?.data?.error_description?.includes(
        "Access is denied"
      )
        ? "Access denied: You do not have permission to perform this activity."
        : err.response?.data?.error_description ||
          err.response?.data?.message ||
          err.message,
      data: []
    };
  }
};

export const fetchInheritedModules = async (
  className: string
): Promise<RequestResponse<string[]>> => {
  try {
    const response = await axiosInstanceTasks.get(
      `/implementation-task-names?className=${className}`
    );
    if (response.data) {
      return {
        error: false,
        data: response.data.data || []
      };
    }
    return {
      error: true,
      message: "Error fetching inherited modules",
      data: []
    };
  } catch (err) {
    console.error("Error fetching inherited modules: ", err);
    return {
      error: true,
      message: err.response?.data?.error_description?.includes(
        "Access is denied"
      )
        ? "Access denied: You do not have permission to perform this activity."
        : err.response?.data?.error_description ||
          err.response?.data?.message ||
          err.message,
      data: []
    };
  }
};

export const uploadFilesApi = async (
  formData: FormData,
  taskClass: string
): Promise<RequestResponse> => {
  try {
    await axiosInstanceTasks.post(`/upload?taskClass=${taskClass}`, formData, {
      headers: {
       'Content-Type': 'multipart/form-data',
        Accept:
          "text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,image/apng,*/*;q=0.8,application/signed-exchange;v=b3;q=0.7"
      }
    });

    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to upload files: ", err);

    if (err.response?.status === 413) {
      return {
        error: true,
        message:
          "The file(s) you are trying to upload are too large. Please reduce the file size and try again.",
        data: null
      };
    }

    return {
      error: true,
      message: err.response?.data?.error_description?.includes(
        "Access is denied"
      )
        ? "Access denied: You do not have permission to perform this activity."
        : err.response?.data?.error_description ||
          err.response?.data?.message ||
          err.message,
      data: err.response?.data.data || null
    };
  }
};
